import React from 'react';
import {
  ArchiveIcon,
  Button,
  Checkbox,
  IconButton,
  Pane,
  SearchIcon,
  Select,
  Table,
  Text,
  toaster,
} from 'evergreen-ui';
import { format, set, subDays } from 'date-fns';
import { VirtualList } from 'Utils/VirtualList';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import {
  useDeleteNotificationsHeaderMutation,
  useLazyGetNotificationsHeadersQuery,
} from 'Services/api/notificationsHeaderApi';
import { useSelector } from 'react-redux';
import { timeZoneSelector } from 'Features/App/appSlice';
import { timeZoneToUTC, UTCtoTimeZone } from 'Utils/UTCConverter';
import { DATE_FORMATS } from 'Utils/formats';
import NotifyTable from 'Shared/Notifications/NotifyTable';

const INPUT_DATETIME = "yyyy-MM-dd'T'HH:mm";
const pageSizes = [10, 20, 50, 100];
const initialParams = {
  startDate: format(subDays(set(new Date(), { hours: 0, minutes: 0, seconds: 0 }), 7), INPUT_DATETIME),
  endDate: format(set(new Date(), { hours: 23, minutes: 59, seconds: 59 }), INPUT_DATETIME),
  size: pageSizes[2],
};

export default function NotificationsCurrent() {
  const [currentPageSize, setCurrentPageSize] = React.useState(initialParams.size);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [dialogData, setDialogData] = React.useState(null);
  const [selectedNotify, setSelectedNotify] = React.useState([]);
  const selectedZone = useSelector(timeZoneSelector);

  const tableHeader = [
    { name: `Дата UTC${selectedZone.selectedUserTimeZone}`, cellProps: { flex: '0 2 25%' } },
    { name: 'Уведомление', cellProps: { flex: '3 2 10%' } },
  ];
  const [params, setParams] = React.useState({
    size: initialParams.size,
    startDate: timeZoneToUTC(new Date(initialParams.startDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
    endDate: timeZoneToUTC(new Date(initialParams.endDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
    page: 0,
  });

  const [deleteNotificationsHeader] = useDeleteNotificationsHeaderMutation();

  const [loadNotificationsHeadersLogs, {
    notifications, totalPages,
    isFetching: isFetchingNotificationHeadersLogs,
    isLoading: isLoadingNotificationHeadersLogs,
  }] = useLazyGetNotificationsHeadersQuery({
    selectFromResult: (result) => ({
      notifications: result.data?.content || [],
      totalPages: result.data?.totalPages || 0,
      ...result,
    }),
  });

  const setChangedParams = React.useCallback((obj = {}) => {
    setParams((curr) => ({
      size: curr.size,
      startDate: curr.startDate,
      endDate: curr.endDate,
      ...obj,
    }));
  }, []);

  React.useEffect(() => {
    loadNotificationsHeadersLogs({
      size: params.size,
      startDate: timeZoneToUTC(new Date(initialParams.startDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
      endDate: timeZoneToUTC(new Date(initialParams.endDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
      page: params.page,
    });
  }, [loadNotificationsHeadersLogs, params.page, params.size, selectedZone.selectedUserTimeZone]);

  const selectPageSize = React.useCallback((e) => {
    setCurrentPageSize(e.target.value);
  }, []);

  const deleteNotifications = React.useCallback(async (ids) => {
    try {
      await deleteNotificationsHeader(ids).unwrap();
      setCurrentPage(0);
      toaster.success(ids.length > 1 ? 'Уведомления отмечены как прочитанные!' : 'Уведомление отмечено как прочитанное!');
    } catch {
      toaster.danger(ids.length > 1 ? 'Ошибка при перемещении уведомлений в прочитанные!' : 'Ошибка при перемещении уведомления в прочитанные!');
    }
  }, [deleteNotificationsHeader]);

  // checkBox напротив уведомления
  const onChangeCheck = React.useCallback((e, id) => {
    setSelectedNotify((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (e.target?.checked) tmp.push(id);
      else tmp.splice(tmp.findIndex((i) => i === id), 1);
      return tmp;
    });
  }, []);

  const searchClick = React.useCallback(() => {
    setChangedParams({ size: currentPageSize, page: 0 });
  }, [currentPageSize, setChangedParams]);

  const onPageChange = React.useCallback((page) => {
    setCurrentPage(page);
    setParams((cur) => ({
      ...cur,
      page,
    }));
    setSelectedNotify([]);
  }, []);

  const notificationIds = React.useMemo(() => notifications.map(({ id }) => id) || [], [notifications]);

  const RowTable = React.useCallback((notification) => {
    const notifyCreateDateTimeUTCCurrent = UTCtoTimeZone(notification?.creationDateTimeUTC, selectedZone.selectedUserTimeZone);
    const createMarkup = () => {
      const newstr = notification?.text?.replace(/\n/g, '<br />');

      return {
        __html: newstr?.length > 60 ? `${newstr.substring(0, 60)}...` : newstr ?? '',
      };
    };

    const onChange = (event) => {
      event.persist();
      onChangeCheck(event, notification?.id);
    };

    if (!notifications?.length) return <Table.Row><Text>Нет уведомлений!</Text></Table.Row>;
    return (
      <Table.Row
        height="auto"
        isSelectable
        onClick={() => {
          setDialogData({ isShown: true, notification });
        }}
      >
        <Table.Cell flexBasis={40} flexGrow={0} flexShrink={0}>
          <Checkbox
            onClick={(e) => e.stopPropagation()}
            checked={!!selectedNotify.find((i) => i === notification?.id)}
            onChange={onChange}
          />
        </Table.Cell>
        <Table.Cell flex="1 1 19%">
          {
            notification?.creationDateTimeUTC
              ? <Text fontWeight="600">{notifyCreateDateTimeUTCCurrent}</Text>
              : ''
          }
        </Table.Cell>
        <Table.Cell flex="3 1 40%">
          <Text fontWeight="600" dangerouslySetInnerHTML={createMarkup()} />
        </Table.Cell>
        <Table.Cell isSelectable={false} flex="1 1 min-content" justifyContent="center">
          <Button
            appearance="minimal"
            iconBefore={ArchiveIcon}
          >
            Прочитать
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  }, [notifications.length, onChangeCheck, selectedNotify, selectedZone.selectedUserTimeZone]);

  const onCheckedAll = (event) => {
    if (event.target.checked) setSelectedNotify(notificationIds);
    else setSelectedNotify([]);
  };

  const onClicked = () => {
    setDialogData({ isShown: true, notifications: selectedNotify });
  };

  const onConfirmDialogData = () => {
    deleteNotifications(dialogData?.notifications?.length ? selectedNotify : [dialogData?.notification?.id]).then();
    setDialogData(null);
    setSelectedNotify([]);
  };

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" gap=".5rem">
      <Pane
        display="grid"
        alignItems="center"
        justifyContent="center"
        gridTemplateColumns="max-content max-content max-content"
        gap=".5rem"
      >
        <Text>показывать по</Text>
        <Select
          value={currentPageSize}
          onChange={selectPageSize}
        >
          {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
        </Select>
        <IconButton
          icon={SearchIcon}
          onClick={searchClick}
        />
      </Pane>
      {
        !isLoadingNotificationHeadersLogs && !isFetchingNotificationHeadersLogs
          ? (
            <NotifyTable
              checked={selectedNotify?.length > 0 && selectedNotify.length === notifications?.length}
              onCheckedAll={onCheckedAll}
              thead={tableHeader}
              onClickedButton={onClicked}
              disabledButton={!selectedNotify.length}
              isShownDialog={dialogData?.isShown}
              dialogData={dialogData?.notification || {}}
              closeDialog={() => setDialogData(null)}
              multiple={!!dialogData?.notifications}
              onConfirmDialogData={onConfirmDialogData}
              currentPage={currentPage}
              onPageChange={onPageChange}
              totalPages={totalPages}
            >
              <MessagesList messages={notifications} RowTable={RowTable} />
            </NotifyTable>
          )
          : <SuspenseSpinner />
      }
    </Pane>
  );
}

const MessagesList = React.memo(({ messages, RowTable, isLoading }) => {
  if (isLoading) return <SuspenseSpinner />;
  if (!messages.length) return <Table.Row><Table.Cell><Text>Нет уведомлений</Text></Table.Cell></Table.Row>;
  return <VirtualList data={messages} row={RowTable} />;
});
MessagesList.displayName = 'MessagesList';
